import React, { useContext, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CreateOutlined,
  StarOutlined,
  StarBorderOutlined
} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import CustomTextfield from './CustomTextfield';
import { response } from '../../api/db';
import {
  adminSendReview,
  sendReview,
  updateEvaluation
} from '../../api/db/evaluation';
import {
  gray10,
  gray70,
  gray80,
  gray90,
  gray100,
  green50,
  primaryButtonBackgroundColor,
  white,
  yellow,
  primaryButtonOutlinedColor
} from '../../constants/Colors';
import { userType as userTypeEnum } from '../../constants/Enum';
import { evaluationSendSchema } from '../../validation/evaluations';
import type {
  EvaluationFormType,
  EvaluationSendType
} from '../../validation/evaluations/evaluationsType';
import { AuthContext } from '../../contexts/auth';
import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import { common } from '../../modules/error';
import { sentryLog } from '../../config/sentry';

interface Props {
  evaluation: EvaluationFormType;
  currentUserType?: number;
  adminFlg?: boolean;
  productId?: number;
  productName?: string;
  setEvaluationData?: React.Dispatch<React.SetStateAction<EvaluationFormType>>;
}

const scoreOptions = [
  { label: '3.0', value: 3 },
  { label: '4.0', value: 4 },
  { label: '5.0', value: 5 }
];

const styles = {
  nicknameDateDivider: {
    backgroundColor: gray70,
    display: 'inline-block',
    height: '16px',
    m: '0 8px',
    width: '1px',
    verticalAlign: 'middle'
  },
  nicknameDateText: {
    color: gray90,
    fontSize: '14px'
  },
  nullText: {
    color: gray90,
    fontSize: '14px',
    mt: '18px'
  },
  reviewButton: {
    boxSizing: 'border-box',
    height: '32px',
    mt: '16px',
    '@media screen and (max-width: 600px)': {
      width: '100%'
    }
  },
  scoreText: {
    color: gray100,
    fontSize: '14px',
    fontWeight: '500'
  },
  select: {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiSelect-select': {
      p: '0 0 0 4px'
    },
    bgcolor: gray10,
    height: '21px',
    width: '100%'
  },
  sendButton: {
    '@media screen and (max-width: 600px)': {
      maxWidth: '320px',
      width: '100%'
    },
    boxSizing: 'border-box',
    height: '44px',
    m: '16px auto 12px',
    width: '320px'
  },
  statusBadge: {
    borderRadius: '50%',
    height: '12px',
    mr: '8px',
    width: '12px'
  },
  statusText: {
    color: gray100,
    fontSize: '16px',
    fontWeight: '500'
  },
  submitNote: {
    color: gray100,
    fontSize: '14px'
  },
  text: {
    color: gray100,
    fontSize: '14px'
  },
  title: {
    color: gray100,
    fontSize: '16px',
    fontWeight: '500',
    m: '4px 0'
  },
  wrapper: {
    backgroundColor: gray10,
    borderRadius: '4px',
    m: '18px 0 24px',
    p: '16px 16px 28px'
  }
};

const ReviewInputBox: React.FC<Props> = (props) => {
  const {
    currentUserType,
    evaluation,
    adminFlg,
    productId,
    productName,
    setEvaluationData
  } = props;

  const navigate = useNavigate();
  const { checkLoginStatus, userId, userRank, userType } =
    useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [infoText, setInfoText] = useState('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  // 表示用スコア
  const [displayData, setDisplayData] = useState<{
    status?: number;
    content?: string;
    nickname?: string;
    title?: string;
    updateDate?: string;
  }>({});
  const [displayScore, setDisplayScore] = useState<number>();
  // 入力フォームの展開
  const [expanded, setExpanded] = useState<boolean>();
  // 口コミ有無
  const [isReviewed, setIsReviewed] = useState<boolean>();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch
  } = useForm<EvaluationSendType>({
    resolver: yupResolver(evaluationSendSchema)
  });

  useEffect(() => {
    reset({
      content: evaluation.content,
      nickname: evaluation.nickname,
      title: evaluation.title
    });
    setDisplayScore(evaluation.score);
    setDisplayData({
      status: evaluation.status,
      content: evaluation.content,
      nickname: evaluation.nickname,
      title: evaluation.title,
      updateDate: evaluation.updateDate
    });
    const isPropsReviewed =
      watch('nickname') === null ||
      watch('content') === null ||
      watch('title') === null
        ? false
        : true;
    setIsReviewed(isPropsReviewed);
  }, [evaluation]);

  useEffect(() => {
    if (adminFlg) {
      setExpanded(true);
      setValue('nickname', '管理者');
    }
  }, [adminFlg]);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('nickname') === undefined ||
        '' ||
        watch('title') === undefined ||
        '' ||
        watch('content') === undefined ||
        '' ||
        Boolean(errors.nickname) ||
        Boolean(errors.title) ||
        Boolean(errors.content)
      );
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    setLoading(true);
    if (!checkLoginStatus) return navigate('/auth/login');
    const result = await checkLoginStatus();
    if (!result.check) {
      navigate('/auth/login');
      return;
    }
    try {
      // 口コミ送信API呼び出し
      let response;
      if (adminFlg) {
        if (!productId) return;
        response = await adminSendReview(productId, watch, evaluation.id);
        setEvaluationData &&
          setEvaluationData({
            id: response.data.id,
            content: response.data.content,
            nickname: '管理者',
            title: response.data.title
          });
        window.dataLayer.push({
          event: 'evaluation',
          screen: window.location.pathname,
          category: 'button',
          action: 'evaluation_evaluation',
          label: productName,
          value: productId,
          user_id: userId,
          user_type: userType,
          user_rank: userRank
        });
      } else {
        if (!evaluation.id) return;
        response = await sendReview(evaluation.id, watch);
      }
      const fetchedData = response.data;

      // 日付の表示形式を変更
      const updateDate = new Date(fetchedData.updated_at);
      const updateDateMonth = updateDate.getMonth() + 1;
      const formattedUpdatedDate =
        updateDateMonth < 10
          ? `${updateDate.getFullYear()}/0${updateDateMonth}/${updateDate.getDate()}`
          : `${updateDate.getFullYear()}/${updateDateMonth}/${updateDate.getDate()}`;
      // 表示用データにセット
      setDisplayData({
        status: fetchedData.status,
        content: fetchedData.content,
        nickname: fetchedData.nickname,
        title: fetchedData.title,
        updateDate: formattedUpdatedDate
      });

      if (!adminFlg) {
        setIsReviewed(true);
        setExpanded(false);
      }
      setInfoText('口コミを送信しました');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      setOpen(true);
      setLoading(false);
    }
  };

  // スコアの更新
  const updateScore = async (
    event: SelectChangeEvent<unknown>
  ): Promise<void> => {
    setLoading(true);
    if (!checkLoginStatus) return navigate('/auth/login');
    const result = await checkLoginStatus();
    if (!result.check) {
      navigate('/auth/login');
      return;
    }
    try {
      // 評価更新API呼び出し
      if (!evaluation.id) return;
      const selectedScore = event.target.value as number;
      const response = await updateEvaluation(evaluation.id, selectedScore);
      setDisplayScore(response.data.score);
      window.dataLayer.push({
        event: 'score',
        screen: window.location.pathname,
        category: 'pulldown',
        action: 'evaluation_score',
        label: productName,
        value: productId,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // 評価の関数
  type StarProps = {
    type: 'filled' | 'outlined';
  };

  const Star = (props: StarProps) => {
    const { type } = props;
    switch (type) {
      case 'filled':
        return <StarOutlined sx={{ color: yellow }} />;
      case 'outlined':
      default:
        return <StarBorderOutlined sx={{ color: gray80 }} />;
    }
  };

  // スコアコンポーネント
  const Rating = (props: { rating: number }) => {
    const { rating } = props;

    const starIcons = new Array(5).fill('outlined').map((_, index) => {
      if (rating - index >= 1) {
        return 'filled';
      }
      return 'outlined';
    });

    return (
      <Typography component="div" sx={{ display: 'flex', mr: '4px' }}>
        {starIcons.map((iconType, index) => (
          <Star type={iconType} key={index} />
        ))}
      </Typography>
    );
  };
  // ステータスコンポーネント
  const ReviewStatus = (props: { status: number }) => {
    const { status } = props;
    let statusText;
    let statusColor;
    switch (status) {
      case 1: // 承認済
        statusText = '承認済';
        statusColor = green50;
        break;
      case 2: // 申請中
        statusText = '申請中';
        statusColor = gray80;
        break;
      case 9: // 非承認
        statusText = '非承認';
        statusColor = gray80;
        break;
      default:
        statusText = '';
        statusColor = gray80;
    }

    return (
      <Grid container alignItems="center" sx={{ mt: '16px' }}>
        <Typography
          component="span"
          sx={[styles.statusBadge, { backgroundColor: statusColor }]}
        ></Typography>
        <Typography sx={styles.statusText}>{statusText}</Typography>
      </Grid>
    );
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Box sx={styles.wrapper} onClick={(e) => e.stopPropagation()}>
        {!adminFlg && (
          <Grid container alignItems="center">
            {displayScore && (
              <>
                <Rating rating={displayScore} />
                {currentUserType !== userTypeEnum.maker && (
                  <FormControl>
                    <Select
                      sx={styles.select}
                      value={displayScore}
                      onChange={updateScore}
                    >
                      {scoreOptions.map((item) => (
                        <MenuItem
                          key={item.value}
                          value={item.value}
                          sx={styles.scoreText}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </Grid>
        )}

        {!expanded && !isReviewed && (
          <>
            <Typography sx={styles.nullText}>
              この建材の口コミはまだ書いていません
            </Typography>
            <Button
              onClick={() => setExpanded(true)}
              disabled={currentUserType === userTypeEnum.maker}
              startIcon={<CreateOutlined sx={{ color: white }} />}
              sx={[primaryButtonBackgroundColor, styles.reviewButton]}
              variant="contained"
            >
              口コミを書く
            </Button>
          </>
        )}

        {!expanded && isReviewed && (
          <>
            <ReviewStatus status={displayData.status ?? 9} />

            <Typography sx={styles.nicknameDateText}>
              {displayData.nickname}
              <Box component="span" sx={styles.nicknameDateDivider}></Box>
              {displayData.updateDate}
            </Typography>

            <Typography sx={styles.title}>{displayData.title}</Typography>
            <Typography sx={styles.text}>{displayData.content}</Typography>
            <Button
              onClick={() => setExpanded(true)}
              disabled={currentUserType === userTypeEnum.maker}
              startIcon={<CreateOutlined />}
              sx={[primaryButtonOutlinedColor, styles.reviewButton]}
              variant="outlined"
            >
              口コミを編集する
            </Button>
          </>
        )}

        {expanded && (
          <Box sx={{ mt: '24px', width: '100%' }}>
            {!adminFlg && (
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.nickname)}
                    formMb="24px"
                    helperText={errors.nickname?.message}
                    labelText="ニックネーム"
                    requiredText="必須"
                    width={'100%'}
                  />
                )}
                control={control}
                name="nickname"
              />
            )}

            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.title)}
                  formMb="24px"
                  helperText={errors.title?.message}
                  labelText="口コミのタイトル"
                  requiredText="必須"
                  width={'100%'}
                />
              )}
              control={control}
              name="title"
            />

            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.content)}
                  fieldType="textareaRow5"
                  formMb="16px"
                  helperText={errors.content?.message}
                  labelText="口コミ"
                  multiline
                  requiredText="必須"
                  rows={5}
                  width={'100%'}
                />
              )}
              control={control}
              name="content"
            />

            <Grid
              container
              direction="column"
              alignItems="center"
              sx={{
                '@media screen and (max-width: 600px)': {
                  width: '100%'
                }
              }}
            >
              <Typography sx={styles.submitNote}>
                {adminFlg
                  ? '口コミ送信後、すぐに公開されます'
                  : '口コミは事務局が内容を確認後、公開されます'}
              </Typography>
              <Button
                disabled={buttonDisabledFlag()}
                onClick={(e) => {
                  e.stopPropagation();
                  setFirstSubmit(true);
                  handleSubmit(async () => await onSubmit())();
                }}
                sx={[primaryButtonBackgroundColor, styles.sendButton]}
                variant="contained"
              >
                口コミを送信する
              </Button>
            </Grid>
          </Box>
        )}
      </Box>
      <CustomSnackbar
        autoHideDuration={6000}
        infoText={infoText}
        onClose={() => setOpen(false)}
        open={open}
        text={errorText}
      />
    </>
  );
};

export default ReviewInputBox;
